import { useState } from 'react';
import { CalendarClock, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  useCreateMaintenanceWindow,
  useDeleteMaintenanceWindow,
  useMaintenanceWindows,
  type Recurrence,
} from '@/hooks/useMaintenanceWindows.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Switch } from '@/components/ui/switch.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';

const RECURRENCES: { value: Recurrence; label: string }[] = [
  { value: 'none', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

/** Convert a datetime-local value (local wall-clock) to RFC3339, or '' if empty. */
function toRfc3339(localValue: string): string {
  if (!localValue) return '';
  return new Date(localValue).toISOString();
}

/**
 * Maintenance windows: scoped, optionally-recurring time ranges that suppress alert
 * paging during planned work. Their page is Organization Admin → Maintenance windows.
 */
export function MaintenanceWindowsSection() {
  const { data: windows, isLoading } = useMaintenanceWindows();
  const create = useCreateMaintenanceWindow();
  const remove = useDeleteMaintenanceWindow();

  const [name, setName] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [recurrence, setRecurrence] = useState<Recurrence>('none');
  const [suppress, setSuppress] = useState(true);

  const canSubmit = name.trim() !== '' && startsAt !== '' && endsAt !== '' && !create.isPending;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        name: name.trim(),
        startsAt: toRfc3339(startsAt),
        endsAt: toRfc3339(endsAt),
        recurrence,
        suppressAlerts: suppress,
      },
      {
        onSuccess: () => {
          setName('');
          setStartsAt('');
          setEndsAt('');
          setRecurrence('none');
          setSuppress(true);
        },
      },
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Maintenance windows</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        During an active window, alerts on the covered scope are recorded but don't page. Useful for
        planned work that isn't tied to a specific change.
      </p>

      {/* Existing windows */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !windows || windows.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-4">No maintenance windows yet.</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {windows.map((w) => (
            <li
              key={w.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{w.name}</span>
                  {w.activeNow && <Badge variant="secondary">Active now</Badge>}
                  {w.recurrence !== 'none' && <Badge variant="outline">{w.recurrence}</Badge>}
                  {!w.suppressAlerts && <Badge variant="outline">No suppression</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {format(new Date(w.startsAt), 'PPp')} → {format(new Date(w.endsAt), 'PPp')}
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => { remove.mutate(w.id); }}
                disabled={remove.isPending}
                aria-label={`Delete ${w.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Create form */}
      <div className="rounded-md border border-border-subtle p-3 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="mw-name">Name</Label>
          <Input
            id="mw-name"
            value={name}
            onChange={(e) => { setName(e.target.value); }}
            placeholder="e.g. Saturday firmware upgrades"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="mw-start">Starts</Label>
            <Input id="mw-start" type="datetime-local" value={startsAt} onChange={(e) => { setStartsAt(e.target.value); }} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mw-end">Ends</Label>
            <Input id="mw-end" type="datetime-local" value={endsAt} onChange={(e) => { setEndsAt(e.target.value); }} />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label>Repeat</Label>
            <Select value={recurrence} onValueChange={(v) => { setRecurrence(v as Recurrence); }}>
              <SelectTrigger className="w-40 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <Switch id="mw-suppress" checked={suppress} onCheckedChange={setSuppress} />
            <Label htmlFor="mw-suppress" className="font-normal">Suppress alerts</Label>
          </div>
          <div className="ml-auto pb-0.5">
            <Button size="sm" onClick={submit} disabled={!canSubmit}>
              {create.isPending ? 'Adding…' : 'Add window'}
            </Button>
          </div>
        </div>
        {create.isError && <p className="text-xs text-destructive">Failed to create window.</p>}
      </div>
    </div>
  );
}
